import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import { Card, CardHeader, CardBody } from '@/shared/components/ui/Card';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Modal } from '@/shared/components/ui/Modal';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { getApiErrorMessage } from '@/shared/api/axios';
import { RefreshCw, TrendingUp, Trophy, Users, ExternalLink, Target, Gift } from 'lucide-react';
import toast from 'react-hot-toast';
import { useScores, useMyScores, useCalculateScores } from '../hooks/useScores';
import {
  getCurrentPeriod,
  summarizeScores,
  scoreEmployeeName,
  scoreEmployeeDept,
  gradeBadgeClass,
  scoreBarClass,
  barWidthPct,
  type PerformanceScore,
  type ScoreSummary,
} from '../performanceScoring';

// ── Types ───────────────────────────────────────────────────────────────────

interface IncentiveContext {
  targetAmount: number;
  revenueGenerated: number;
  achievedPercent: number;
  incentivePercent: number;
  incentiveAmount: number;
  clientDealsCount: number;
}

/** Extract the employee ID string from a score's employeeId field (may be a string, object, or null). */
function scoreEmpId(score: { employeeId?: { _id?: string } | string | null }): string {
  if (!score.employeeId) return '';
  if (typeof score.employeeId === 'string') return score.employeeId;
  return score.employeeId._id ?? '';
}

// ── Safe targets normalization ──────────────────────────────────────────────

function normalizeTargetsArray(raw: any): any[] {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw.data)) return raw.data;
  return [];
}

// ── Incentive data hook ────────────────────────────────────────────────────

function useIncentiveTargets(period?: string) {
  return useQuery({
    queryKey: ['hr', 'incentive-targets', period],
    queryFn: async () => {
      const p = period ? `?period=${period}` : '';
      const { data } = await api.get(`/api/hr-incentives/targets${p}`);
      return data;
    },
    select: (res) => normalizeTargetsArray(res),
    enabled: !!period,
  });
}

function useMyIncentiveTargets() {
  return useQuery({
    queryKey: ['hr', 'my-targets'],
    queryFn: async () => {
      const { data } = await api.get('/api/hr-incentives/targets/mine');
      return data;
    },
    select: (res) => normalizeTargetsArray(res),
  });
}

/**
 * Build a lookup map from employeeId → IncentiveContext for a given period.
 */
function buildIncentiveLookup(rawTargets: any): Map<string, IncentiveContext> {
  const map = new Map<string, IncentiveContext>();
  const targets = normalizeTargetsArray(rawTargets);
  for (const t of targets) {
    if (!t) continue;
    const empId = typeof t.employeeId === 'object' ? t.employeeId?._id : t.employeeId;
    if (!empId) continue;
    map.set(String(empId), {
      targetAmount: Number(t.targetAmount) || 0,
      revenueGenerated: Number(t.revenueGenerated) || 0,
      achievedPercent: Number(t.achievedPercent) || 0,
      incentivePercent: Number(t.incentivePercent) || 0,
      incentiveAmount: Number(t.incentiveAmount) || 0,
      clientDealsCount: Array.isArray(t.clientDeals) ? t.clientDeals.length : 0,
    });
  }
  return map;
}

/**
 * Build a period-scoped lookup from "employeeId:period" → IncentiveContext.
 */
function buildPeriodIncentiveLookup(rawTargets: any): Map<string, IncentiveContext> {
  const map = new Map<string, IncentiveContext>();
  const targets = normalizeTargetsArray(rawTargets);
  for (const t of targets) {
    if (!t) continue;
    const empId = typeof t.employeeId === 'object' ? t.employeeId?._id : t.employeeId;
    if (!empId || !t.period) continue;
    const key = `${empId}:${t.period}`;
    map.set(key, {
      targetAmount: Number(t.targetAmount) || 0,
      revenueGenerated: Number(t.revenueGenerated) || 0,
      achievedPercent: Number(t.achievedPercent) || 0,
      incentivePercent: Number(t.incentivePercent) || 0,
      incentiveAmount: Number(t.incentiveAmount) || 0,
      clientDealsCount: Array.isArray(t.clientDeals) ? t.clientDeals.length : 0,
    });
  }
  return map;
}

// ── Presentational pieces ──────────────────────────────────────────────────

export function ScoreGradeBadge({ grade }: { grade: string }) {
  return <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-bold ${gradeBadgeClass(grade)}`}>{grade}</span>;
}

const TRACK_LABEL: Record<string, string> = { sales: 'Sales', engineering: 'Engineering' };

/** Incentive context panel for the score breakdown modal (sales track only). */
function IncentiveBreakdown({ incentive, score }: { incentive: IncentiveContext; score: PerformanceScore }) {
  const isSales = score.track === 'sales';
  if (!isSales || !incentive) return null;

  return (
    <div className="rounded-lg border bg-gradient-to-br from-blue-50 to-purple-50 p-4 space-y-2">
      <div className="flex items-center gap-2 mb-1">
        <Target className="h-4 w-4 text-blue-600" />
        <span className="font-semibold text-sm">Incentive Summary — {score.period}</span>
      </div>

      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
        <span className="text-muted-foreground">Monthly Target</span>
        <span className="font-medium text-right tabular-nums">₹{incentive.targetAmount.toLocaleString('en-IN')}</span>

        <span className="text-muted-foreground">Revenue Generated</span>
        <span className="font-medium text-right tabular-nums text-green-700">₹{incentive.revenueGenerated.toLocaleString('en-IN')}</span>

        <span className="text-muted-foreground">Achievement</span>
        <span className={`font-bold text-right ${incentive.achievedPercent >= 100 ? 'text-green-600' : incentive.achievedPercent >= 70 ? 'text-yellow-600' : 'text-red-600'}`}>
          {incentive.achievedPercent}%
        </span>

        <span className="text-muted-foreground">Incentive Rate</span>
        <span className="font-medium text-right">{incentive.incentivePercent}%</span>

        <span className="text-muted-foreground flex items-center gap-1">
          <Gift className="h-3.5 w-3.5" />
          Incentive Earned
        </span>
        <span className="font-bold text-right text-purple-700">₹{incentive.incentiveAmount.toLocaleString('en-IN')}</span>

        {incentive.clientDealsCount > 0 && (
          <>
            <span className="text-muted-foreground">Client Deals</span>
            <span className="font-medium text-right">{incentive.clientDealsCount}</span>
          </>
        )}
      </div>
    </div>
  );
}

/** Employee self-view / modal breakdown rendered as accessible CSS bars. */
export function ScoreBreakdown({ score, incentive }: { score: PerformanceScore; incentive?: IncentiveContext }) {
  return (
    <div className="space-y-3" data-testid="score-breakdown">
      <p className="text-xs text-muted-foreground">
        {TRACK_LABEL[score.track] ?? score.track} track · {score.period}
      </p>

      {score.breakdown.map((b) => (
        <div key={b.key}>
          <div className="mb-1 flex items-center justify-between text-sm">
            <span className="font-medium">
              {b.label} <span className="text-xs text-muted-foreground">(weight {b.weight})</span>
            </span>
            <span className="tabular-nums text-muted-foreground">
              {b.hasData ? `${b.value}%` : 'No data'} · {b.points} pts
            </span>
          </div>
          <div
            className="h-2 w-full overflow-hidden rounded-full bg-gray-200"
            role="progressbar"
            aria-valuenow={b.value}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={b.label}
          >
            <div className={`h-full rounded-full ${scoreBarClass(b.value)}`} style={{ width: `${barWidthPct(b.value)}%` }} />
          </div>
        </div>
      ))}

      {score.warningsCount > 0 && (
        <p className="text-sm font-medium text-red-600">
          − {score.warningPenalty} pts from {score.warningsCount} warning{score.warningsCount === 1 ? '' : 's'} this period
        </p>
      )}

      <IncentiveBreakdown incentive={incentive ?? ({ targetAmount: 0, revenueGenerated: 0, achievedPercent: 0, incentivePercent: 0, incentiveAmount: 0, clientDealsCount: 0 } as IncentiveContext)} score={score} />

      <div className="flex items-center justify-between border-t pt-2">
        <span className="font-semibold">Final Score</span>
        <span className="flex items-center gap-2">
          <span className="text-lg font-bold tabular-nums">{score.score}</span>
          <ScoreGradeBadge grade={score.grade} />
        </span>
      </div>
    </div>
  );
}

export function ScoresSummaryCards({ summary }: { summary: ScoreSummary }) {
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
      <div className="rounded-lg border bg-blue-50 p-4">
        <p className="flex items-center gap-1 text-xs text-muted-foreground">
          <TrendingUp className="h-3.5 w-3.5" />Average Score
        </p>
        <p className="text-xl font-bold tabular-nums">{summary.average}</p>
      </div>
      <div className="rounded-lg border bg-emerald-50 p-4">
        <p className="flex items-center gap-1 text-xs text-muted-foreground">
          <Trophy className="h-3.5 w-3.5" />Top Performer
        </p>
        <p className="truncate text-xl font-bold text-emerald-700">
          {summary.topPerformer ? `${summary.topPerformer.name} (${summary.topPerformer.score})` : '—'}
        </p>
      </div>
      <div className="rounded-lg border bg-purple-50 p-4">
        <p className="flex items-center gap-1 text-xs text-muted-foreground">
          <Users className="h-3.5 w-3.5" />Employees Scored
        </p>
        <p className="text-xl font-bold text-purple-700 tabular-nums">{summary.count}</p>
        {summary.byDepartment.length > 0 && (
          <p className="mt-1 truncate text-xs capitalize text-muted-foreground">
            {summary.byDepartment.map((d) => `${d.department}: ${d.average}`).join(' · ')}
          </p>
        )}
      </div>
    </div>
  );
}

export function ScoresTable({
  scores,
  onSelect,
  incentiveLookup,
}: {
  scores: PerformanceScore[];
  onSelect?: (s: PerformanceScore) => void;
  incentiveLookup?: Map<string, IncentiveContext>;
}) {
  return (
    <table className="w-full text-left text-sm">
      <thead className="sticky top-0 bg-muted/50 text-xs uppercase">
        <tr>
          <th className="px-4 py-3">Representative</th>
          <th className="px-4 py-3">Department / Role</th>
          <th className="px-4 py-3">Performance Score</th>
          <th className="px-4 py-3">Grade</th>
          <th className="px-4 py-3 text-right">Target</th>
          <th className="px-4 py-3 text-right">Revenue</th>
          <th className="px-4 py-3 text-right">Achievement</th>
          <th className="px-4 py-3 text-right">Incentive</th>
          <th className="px-4 py-3 text-right">Warnings</th>
        </tr>
      </thead>
      <tbody className="divide-y">
        {scores.length === 0 && (
          <tr>
            <td colSpan={9} className="px-4 py-8 text-center text-muted-foreground">
              No BDE or Sales representative scores for this period yet. Click &quot;Calculate Scores&quot; to generate scores.
            </td>
          </tr>
        )}
        {scores.map((s) => {
          const empId = scoreEmpId(s);
          const incentive = empId ? incentiveLookup?.get(empId) : undefined;
          // Fall back to score.metrics (populated by the scoring engine after Calculate Scores)
          const targetAmt = incentive?.targetAmount ?? (s.track === 'sales' ? (s.metrics?.targetAmount ?? 0) : 0);
          const revenue = incentive?.revenueGenerated ?? (s.track === 'sales' ? (s.metrics?.periodRevenue ?? 0) : 0);
          const achievedPct = incentive?.achievedPercent ?? (s.track === 'sales' && targetAmt > 0 ? Math.round((revenue / targetAmt) * 100) : 0);
          const incentiveAmt = incentive?.incentiveAmount ?? (s.track === 'sales' ? Math.round(revenue * ((s.metrics?.incentivePercent ?? 5) / 100)) : 0);
          return (
            <tr
              key={s._id}
              className={onSelect ? 'cursor-pointer hover:bg-muted/20' : ''}
              onClick={onSelect ? () => onSelect(s) : undefined}
            >
              <td className="px-4 py-3 font-medium">{scoreEmployeeName(s)}</td>
              <td className="px-4 py-3 capitalize text-muted-foreground">{scoreEmployeeDept(s) || 'Sales (BDE)'}</td>
              <td className="px-4 py-3">
                <div className="flex items-center gap-2">
                  <div className="h-2 w-20 overflow-hidden rounded-full bg-gray-200">
                    <div className={`h-full rounded-full ${scoreBarClass(s.score)}`} style={{ width: `${barWidthPct(s.score)}%` }} />
                  </div>
                  <span className="tabular-nums font-semibold">{s.score}</span>
                  {!s.hasData && <span className="text-xs text-muted-foreground">(no data)</span>}
                </div>
              </td>
              <td className="px-4 py-3"><ScoreGradeBadge grade={s.grade} /></td>
              <td className="px-4 py-3 text-right tabular-nums">₹{targetAmt.toLocaleString('en-IN')}</td>
              <td className="px-4 py-3 text-right tabular-nums text-green-700">₹{revenue.toLocaleString('en-IN')}</td>
              <td className="px-4 py-3 text-right">
                <span className={`font-bold ${achievedPct >= 100 ? 'text-green-600' : achievedPct >= 70 ? 'text-yellow-600' : 'text-red-600'}`}>
                  {achievedPct}%
                </span>
              </td>
              <td className="px-4 py-3 text-right">
                <span className="font-semibold text-purple-700">₹{incentiveAmt.toLocaleString('en-IN')}</span>
              </td>
              <td className="px-4 py-3 text-right tabular-nums">{s.warningsCount > 0 ? s.warningsCount : '—'}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// ── Containers (data-fetching) ─────────────────────────────────────────────

function AdminScoresView() {
  const [period, setPeriod] = useState(getCurrentPeriod());
  const [selected, setSelected] = useState<PerformanceScore | null>(null);
  const { data, isLoading, isError, refetch } = useScores(period);
  const calcMut = useCalculateScores();
  const { data: incentiveTargets } = useIncentiveTargets(period);

  const incentiveLookup = buildIncentiveLookup(incentiveTargets ?? []);
  const selectedIncentive = selected ? incentiveLookup.get(scoreEmpId(selected)) : undefined;

  const handleCalculate = async () => {
    try {
      const res = await calcMut.mutateAsync(period);
      toast.success(`Calculated ${res.scored} score${res.scored === 1 ? '' : 's'} for ${period}.`);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to calculate scores.'));
    }
  };

  const allScores = data?.data ?? [];
  // Restrict to BDE & Sales Representatives
  const scores = allScores.filter((s) => s.track === 'sales' || String(s.department ?? '').toLowerCase() === 'sales');
  const summary = summarizeScores(scores);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <Input name="score-period" label="Period" type="month" value={period} onChange={(e) => setPeriod(e.target.value)} className="w-44" />
        <div className="ml-auto flex items-end gap-2">
          <Button size="sm" variant="outline" onClick={() => refetch()}>
            <RefreshCw className="mr-1 h-4 w-4" />Refresh
          </Button>
          <Button size="sm" onClick={handleCalculate} loading={calcMut.isPending}>
            <TrendingUp className="mr-1 h-4 w-4" />Calculate Scores
          </Button>
        </div>
      </div>

      <ScoresSummaryCards summary={summary} />

      {scores.length === 0 && (incentiveTargets?.length ?? 0) > 0 && (
        <div className="flex items-center gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3">
          <TrendingUp className="h-5 w-5 text-blue-600 shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-blue-800">Incentive targets exist for {period} but scores haven't been calculated yet.</p>
            <p className="text-xs text-blue-600">Click &quot;Calculate Scores&quot; to generate performance scores using the latest incentive data.</p>
          </div>
          <Button size="sm" onClick={handleCalculate} loading={calcMut.isPending}>
            <TrendingUp className="mr-1 h-4 w-4" />Calculate Now
          </Button>
        </div>
      )}

      <Card className="border-none shadow-sm">
        <CardHeader title={`BDE Sales Performance Scores (${scores.length})`} subtitle={`Period ${period} · Target achievement & revenue scoring`} />
        <CardBody className="p-0">
          {isLoading ? (
            <div className="p-4"><Skeleton className="h-[280px]" /></div>
          ) : isError ? (
            <p className="px-4 py-8 text-center text-red-600">Could not load scores. Please retry.</p>
          ) : (
            <div className="max-h-[500px] overflow-auto">
              <ScoresTable scores={scores} onSelect={setSelected} incentiveLookup={incentiveLookup} />
            </div>
          )}
        </CardBody>
      </Card>

      <Modal open={!!selected} onClose={() => setSelected(null)} title={selected ? `${scoreEmployeeName(selected)} — Score Breakdown` : ''}>
        {selected && <div className="py-2"><ScoreBreakdown score={selected} incentive={selectedIncentive} /></div>}
      </Modal>
    </div>
  );
}

function EmployeeScoresView() {
  const { data, isLoading } = useMyScores();
  const { data: myIncentiveTargets } = useMyIncentiveTargets();
  const [selected, setSelected] = useState<PerformanceScore | null>(null);

  // Build a period-scoped lookup from my targets
  const incentiveLookup = buildPeriodIncentiveLookup(myIncentiveTargets ?? []);

  if (isLoading) return <Skeleton className="h-[300px]" />;

  const scores = [...(data?.data ?? [])].sort((a, b) => b.period.localeCompare(a.period));
  const latest = scores[0] ?? null;
  const targetList = normalizeTargetsArray(myIncentiveTargets);
  const latestTarget = targetList[0];
  const latestTargetPeriod = latestTarget?.period ?? '';
  const latestTargetCtx: IncentiveContext | undefined = latestTarget
    ? {
        targetAmount: latestTarget.targetAmount,
        revenueGenerated: latestTarget.revenueGenerated,
        achievedPercent: latestTarget.achievedPercent,
        incentivePercent: latestTarget.incentivePercent,
        incentiveAmount: latestTarget.incentiveAmount,
        clientDealsCount: (latestTarget.clientDeals || []).length,
      }
    : undefined;

  if (!latest) {
    return (
      <Card className="border-none shadow-sm">
        <CardBody className="py-8 text-center text-muted-foreground">
          <p>No performance score has been calculated for you yet.</p>
          {latestTargetCtx && latestTargetCtx.targetAmount > 0 && (
            <div className="mt-4">
              <IncentiveBreakdown incentive={latestTargetCtx} score={{ track: 'sales', period: latestTargetPeriod, _id: '', employeeId: null, score: 0, grade: 'F', rawScore: 0, warningsCount: 0, warningPenalty: 0, breakdown: [], hasData: false } as PerformanceScore} />
            </div>
          )}
        </CardBody>
      </Card>
    );
  }

  const latestIncentive = incentiveLookup.get(`${scoreEmpId(latest)}:${latest.period}`);

  return (
    <div className="space-y-4">
      <Card className="border-none shadow-sm">
        <CardHeader
          title="My Latest Performance Score"
          subtitle={`Period ${latest.period}`}
          action={
            <a
              href="/app/hr/incentives"
              className="flex items-center gap-1 text-xs text-blue-600 hover:underline"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              View incentive details
            </a>
          }
        />
        <CardBody>
          <ScoreBreakdown score={latest} incentive={latestIncentive} />
        </CardBody>
      </Card>

      {scores.length > 1 && (
        <Card className="border-none shadow-sm">
          <CardHeader title="History" />
          <CardBody className="p-0">
            <table className="w-full text-left text-sm">
              <thead className="bg-muted/50 text-xs uppercase">
                <tr>
                  <th className="px-4 py-3">Period</th>
                  <th className="px-4 py-3">Score</th>
                  <th className="px-4 py-3">Grade</th>
                  <th className="px-4 py-3 text-right">Incentive Earned</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {scores.slice(1).map((s) => {
                  const inc = incentiveLookup.get(`${scoreEmpId(s)}:${s.period}`);
                  return (
                    <tr key={s._id} className="cursor-pointer hover:bg-muted/20" onClick={() => setSelected(s)}>
                      <td className="px-4 py-3 font-medium">{s.period}</td>
                      <td className="px-4 py-3 tabular-nums">{s.score}</td>
                      <td className="px-4 py-3"><ScoreGradeBadge grade={s.grade} /></td>
                      <td className="px-4 py-3 text-right">
                        {inc && s.track === 'sales' ? (
                          <span className="font-semibold text-purple-700">₹{inc.incentiveAmount.toLocaleString('en-IN')}</span>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardBody>
        </Card>
      )}

      <Modal open={!!selected} onClose={() => setSelected(null)} title={selected ? `Score Breakdown — ${selected.period}` : ''}>
        {selected && (
          <div className="py-2">
            <ScoreBreakdown score={selected} incentive={incentiveLookup.get(`${scoreEmpId(selected)}:${selected.period}`)} />
          </div>
        )}
      </Modal>
    </div>
  );
}

export function PerformanceScoresTab({ isAdmin }: { isAdmin: boolean }) {
  return isAdmin ? <AdminScoresView /> : <EmployeeScoresView />;
}
